import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Switch,
  Select,
  Button,
  useToast,
} from "@chakra-ui/react";
import _ from "lodash";
import { cqAPI } from "../../lib/Auth";

// We give Webchat just two options
const webchatOptions = [
  { label: "Right away", value: 0 },
  { label: "1 minute", value: 1 },
];

// SMS/Email have more options
const smsEmailOptions = [
  { label: "Right away", value: 0 },
  { label: "15 minutes", value: 15 },
  { label: "1 hour", value: 60 },
  { label: "2 hours", value: 120 },
];

const AutopilotSettings = () => {
  const [settings, setSettings] = useState({
    webchat: {
      after: { enabled: false, delay: 1 },
      during: { enabled: false, delay: 1 },
    },
    sms: {
      after: { enabled: false, delay: 1 },
      during: { enabled: false, delay: 1 },
    },
    email: {
      after: { enabled: false, delay: 1 },
      during: { enabled: false, delay: 1 },
    },
    voicemail: {
      after: { enabled: false, delay: 1 },
      during: { enabled: false, delay: 1 },
    },
  });

  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const toast = useToast();

  // ---------------------------
  //  Fetch existing settings
  // ---------------------------
  const fetchSettings = async () => {
    try {
      const response = await cqAPI.get("/settings/business?id=auto_response");

      if (response?.data) {
        // Might be { type: "ok", data: {...} } or just { webchat, sms, email }
        const serverData = response.data.webchat ? response.data : response.data.data;

        // Convert server seconds to UI minutes
        const newSettings = {
          webchat: {
            after: {
              enabled: serverData.webchat?.after?.enabled ?? false,
              delay: (serverData.webchat?.after?.respond_after ?? 60) / 60,
            },
            during: {
              enabled: serverData.webchat?.during?.enabled ?? false,
              delay: (serverData.webchat?.during?.respond_after ?? 60) / 60,
            },
          },
          sms: {
            after: {
              enabled: serverData.sms?.after?.enabled ?? false,
              delay: (serverData.sms?.after?.respond_after ?? 60) / 60,
            },
            during: {
              enabled: serverData.sms?.during?.enabled ?? false,
              delay: (serverData.sms?.during?.respond_after ?? 60) / 60,
            },
          },
          email: {
            after: {
              enabled: serverData.email?.after?.enabled ?? false,
              delay: (serverData.email?.after?.respond_after ?? 60) / 60,
            },
            during: {
              enabled: serverData.email?.during?.enabled ?? false,
              delay: (serverData.email?.during?.respond_after ?? 60) / 60,
            },
          },
          voicemail: {
            after: {
              enabled: serverData.voicemail?.after?.enabled ?? false,
              delay: (serverData.voicemail?.after?.respond_after ?? 60) / 60,
            },
            during: {
              enabled: serverData.voicemail?.during?.enabled ?? false,
              delay: (serverData.voicemail?.during?.respond_after ?? 60) / 60,
            },
          },
        };

        setSettings(newSettings);
        setOriginalSettings(_.cloneDeep(newSettings));
      }
    } catch (error) {
      console.error("Failed to fetch autopilot settings:", error);
      toast({
        title: "Error",
        description: "Could not fetch autopilot settings.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // ---------------------------
  //  Track changes
  // ---------------------------
  useEffect(() => {
    const changesDetected = !_.isEqual(settings, originalSettings);
    setHasChanges(changesDetected);
  }, [settings, originalSettings]);

  // ---------------------------
  //  Event handlers
  // ---------------------------
  const handleSwitchChange = (channel, timePeriod, checked) => {
    setSettings((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [timePeriod]: {
          ...prev[channel][timePeriod],
          enabled: checked,
        },
      },
    }));
  };

  const handleDelaySelect = (channel, timePeriod, value) => {
    // value is a string from <Select>, parse it
    const delayAsNumber = parseInt(value, 10) || 0;
    setSettings((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [timePeriod]: {
          ...prev[channel][timePeriod],
          delay: delayAsNumber,
        },
      },
    }));
  };

  // ---------------------------
  //  Save changes
  // ---------------------------
  const saveSettings = async () => {
    try {
      // Convert minutes -> seconds
      const toServer = {
        webchat: {
          after: {
            enabled: settings.webchat.after.enabled,
            respond_after: settings.webchat.after.delay * 60,
          },
          during: {
            enabled: settings.webchat.during.enabled,
            respond_after: settings.webchat.during.delay * 60,
          },
        },
        sms: {
          after: {
            enabled: settings.sms.after.enabled,
            respond_after: settings.sms.after.delay * 60,
          },
          during: {
            enabled: settings.sms.during.enabled,
            respond_after: settings.sms.during.delay * 60,
          },
        },
        email: {
          after: {
            enabled: settings.email.after.enabled,
            respond_after: settings.email.after.delay * 60,
          },
          during: {
            enabled: settings.email.during.enabled,
            respond_after: settings.email.during.delay * 60,
          },
        },
        voicemail: {
          after: {
            enabled: settings.voicemail.after.enabled,
            respond_after: settings.voicemail.after.delay * 60,
          },
          during: {
            enabled: settings.voicemail.during.enabled,
            respond_after: settings.voicemail.during.delay * 60,
          },
        },
      };

      const response = await cqAPI.put("/settings/business", {
        key: "auto_response",
        value: toServer,
      });
      
      if (response.type === 'error'){
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return
      }

      setOriginalSettings(_.cloneDeep(settings));
      setHasChanges(false);

      toast({
        title: "Saved",
        description: "Your Autopilot settings were updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to save autopilot settings:", error);
      toast({
        title: "Error",
        description: "Could not save autopilot settings.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ---------------------------
  //  Rendering helpers
  // ---------------------------
  const renderTimePeriodControls = (channelKey, timePeriodKey, label) => {
    const channelData = settings[channelKey][timePeriodKey];
    const isWebchat = channelKey === "webchat";
    // Webchat has 2 options; SMS/Email have 4
    const options = isWebchat
      ? webchatOptions
      : smsEmailOptions;

    return (
      <Flex
        id={`${channelKey}-${timePeriodKey}`}
        direction="column"
        flex="1" // Make each block take equal width
        mr={{ base: 0, md: 4 }}
        mb={{ base: 4, md: 0 }}
      >
        <Text mb={1} fontWeight="bold">
          {label}
        </Text>
        <Switch
          className={`${timePeriodKey}-toggle`}
          id={`${channelKey}-${timePeriodKey}-toggle`}
          isChecked={channelData.enabled}
          onChange={(e) =>
            handleSwitchChange(channelKey, timePeriodKey, e.target.checked)
          }
          mb={2}
        />

        {/* 
          Even if it's disabled, we keep the dropdown rendered 
          so the layout doesn't shift. 
          We just set it disabled if !enabled.
        */}
        <Box>
          <Text fontSize="sm" mb={1}>
            Auto-respond after:
          </Text>
          <Select
            className={`${timePeriodKey}-autopilot-select`}
            id={`${channelKey}-${timePeriodKey}-select`}
            size="sm"
            isDisabled={!channelData.enabled}
            value={channelData.delay}
            onChange={(e) =>
              handleDelaySelect(channelKey, timePeriodKey, e.target.value)
            }
            width="160px"
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>

          {isWebchat && (
            <Text fontSize="xs" mt={1} color="gray.500">
              Responding later than 1 minute for webchat is not recommended
            </Text>
          )}
        </Box>
      </Flex>
    );
  };

  const renderChannelRow = (channelKey, label) => (
    <div id={channelKey} >
      <Box
        key={channelKey}
        w="100%"
        p={4}
        mb={4}
        borderWidth="1px"
        borderRadius="md"
        boxShadow="sm"
      >
        <Text fontSize="lg" mb={4} fontWeight="bold">
          {label}
        </Text>
        {/* Two blocks side by side (During, After).
            Each block gets flex="1" so they share row space evenly. */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justifyContent="flex-start"
          alignItems="flex-start"
          w="100%"
        >
          {renderTimePeriodControls(channelKey, "during", "During Business Hours")}
          {renderTimePeriodControls(channelKey, "after", "After Hours")}
        </Flex>
      </Box>
    </div>
  );

  return (
    <Box p={4} width="100%">
      <Text fontSize="2xl" fontWeight="bold">
        Autopilot Settings
      </Text>

      <Flex justifyContent="flex-end" mb={4}>
        <Button
          id={"save-btn"}
          colorScheme="blue"
          onClick={saveSettings}
          isDisabled={!hasChanges}
        >
          Save
        </Button>
      </Flex>

      {renderChannelRow("webchat", "Webchat")}
      {renderChannelRow("sms", "Text Messages")}
      {renderChannelRow("email", "Email")}
      {renderChannelRow("voicemail", "Voicemail")}
    </Box>
  );
};

export default AutopilotSettings;
